import { ArrowLeft, Loader2, KeyRound, Check, Shield } from 'lucide-react'
import { FormEvent, useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDeviceId, useAppDispatch, useAppSelector } from '../../core'
import {
  fetchCurrentUserAction,
  selectCurrentEmail,
  selectVerificationState,
  verifyOtpAction,
} from '../slices'

interface OtpVerificationFormProps {
  onBack: () => void
}

export const OtpVerificationForm: React.FC<OtpVerificationFormProps> = ({ onBack }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { isVerifying, error } = useAppSelector(selectVerificationState)
  const currentEmail = useAppSelector(selectCurrentEmail)
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [isComplete, setIsComplete] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus()
  }, [])

  useEffect(() => {
    const otpString = otp.join('')
    setIsComplete(otpString.length === 6)
  }, [otp])

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1)
    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData) {
      const newOtp = [...otp]
      pastedData.split('').forEach((char, i) => {
        if (i < 6) newOtp[i] = char
      })
      setOtp(newOtp)
      inputRefs.current[Math.min(pastedData.length, 5)]?.focus()
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const otpString = otp.join('')
    if (otpString.length !== 6 || !currentEmail) return

    try {
      await dispatch(
        verifyOtpAction({
          email: currentEmail,
          deviceId: getDeviceId(),
          code: otpString,
        })
      ).unwrap()

      // Fetch user data after successful verification
      await dispatch(fetchCurrentUserAction()).unwrap()

      navigate('/')
    } catch (err) {
      console.error('OTP verification failed:', err)
      // Shake and clear on error
      setOtp(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    }
  }

  return (
    <form className="otp-form" onSubmit={handleSubmit}>
      <div className="otp-header">
        <div className="otp-icon-wrapper">
          <KeyRound className="otp-icon" />
        </div>
        <div className="otp-instructions">
          <p className="instruction-title">Verification Required</p>
          <p className="instruction-text">
            Enter the 6-digit code sent to <span className="email-highlight">{currentEmail}</span>
          </p>
        </div>
      </div>

      <div className={`otp-inputs-container ${error ? 'has-error' : ''}`}>
        <div className="otp-inputs">
          {otp.map((digit, index) => (
            <div key={index} className={`otp-input-wrapper ${digit ? 'filled' : ''}`}>
              <input
                ref={el => {
                  inputRefs.current[index] = el
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isVerifying}
                className="otp-input"
                aria-label={`Digit ${index + 1}`}
              />
              {index === 2 && <div className="otp-separator" />}
            </div>
          ))}
        </div>
        {isComplete && !error && (
          <div className="completion-indicator">
            <Check className="h-4 w-4" />
            <span>Ready to verify</span>
          </div>
        )}
      </div>

      {error && (
        <div className="otp-error-container">
          <Shield className="error-shield" />
          <div className="error-content">
            <div className="error-title">Authentication Failed</div>
            <div className="error-message">{error}</div>
          </div>
        </div>
      )}

      <div className="otp-actions">
        <button type="button" onClick={onBack} disabled={isVerifying} className="back-button">
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>

        <button
          type="submit"
          disabled={!isComplete || !currentEmail || isVerifying}
          className="verify-button"
        >
          <span className="button-content">
            {isVerifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <Shield className="h-4 w-4" />
                <span>Verify Access</span>
              </>
            )}
          </span>
        </button>
      </div>

      <style>{`
        /* Forced-dark login splash: zinc neutrals are intentionally literal
           (app theme tokens flip in light mode). Brand/status colors reference
           mode-invariant tokens: --primary, --accent, --success, --destructive. */
        .otp-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        /* OTP Header */
        .otp-header {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          padding: 0.875rem;
          background: hsl(var(--primary) / 0.08);
          border: 1px solid hsl(var(--primary) / 0.15);
          border-radius: 8px;
        }

        .otp-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: hsl(var(--primary) / 0.15);
          border-radius: 8px;
          flex-shrink: 0;
        }

        .otp-icon {
          width: 20px;
          height: 20px;
          color: hsl(var(--primary));
        }

        .otp-instructions {
          flex: 1;
        }

        .instruction-title {
          color: #FAFAFA;
          font-family: 'Satoshi', system-ui, sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
          margin-bottom: 0.125rem;
        }

        .instruction-text {
          color: #A1A1AA;
          font-size: 0.8rem;
          line-height: 1.4;
        }

        .email-highlight {
          color: hsl(var(--primary));
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-size: 0.75rem;
        }

        /* OTP Inputs */
        .otp-inputs-container {
          position: relative;
        }

        .otp-inputs {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
        }

        .otp-input-wrapper {
          position: relative;
        }

        .otp-input {
          width: 44px;
          height: 52px;
          background: #0A0A0F;
          border: 1px solid #27272A;
          border-radius: 6px;
          color: #FAFAFA;
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-size: 1.25rem;
          font-weight: 600;
          text-align: center;
          outline: none;
          transition: all 0.2s ease;
          caret-color: hsl(var(--primary));
        }

        .otp-input:focus {
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 3px hsl(var(--primary) / 0.1);
        }

        .otp-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .otp-input-wrapper.filled .otp-input {
          border-color: hsl(var(--success));
          background: hsl(var(--success) / 0.05);
        }

        .otp-separator {
          position: absolute;
          right: -10px;
          top: 50%;
          transform: translateY(-50%);
          width: 6px;
          height: 2px;
          background: #3F3F46;
          border-radius: 1px;
        }

        .has-error .otp-input {
          border-color: hsl(var(--destructive));
          animation: shake 0.4s ease;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-3px); }
          40% { transform: translateX(3px); }
          60% { transform: translateX(-2px); }
          80% { transform: translateX(2px); }
        }

        .completion-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          margin-top: 0.75rem;
          color: hsl(var(--success));
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-size: 0.7rem;
        }

        /* Error state */
        .otp-error-container {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          padding: 0.75rem;
          background: hsl(var(--destructive) / 0.1);
          border: 1px solid hsl(var(--destructive) / 0.25);
          border-radius: 6px;
        }

        .error-shield {
          width: 20px;
          height: 20px;
          color: hsl(var(--destructive));
          flex-shrink: 0;
        }

        .error-content {
          flex: 1;
        }

        .error-title {
          color: hsl(var(--destructive));
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-weight: 500;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.125rem;
        }

        .error-message {
          color: #A1A1AA;
          font-size: 0.8rem;
        }

        /* Action buttons */
        .otp-actions {
          display: flex;
          gap: 0.75rem;
        }

        .back-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          flex: 1;
          padding: 0.75rem;
          background: transparent;
          border: 1px solid #27272A;
          border-radius: 6px;
          color: #A1A1AA;
          font-family: 'Satoshi', system-ui, sans-serif;
          font-weight: 500;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .back-button:not(:disabled):hover {
          background: #27272A;
          color: #FAFAFA;
        }

        .back-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .verify-button {
          flex: 2;
          padding: 0.75rem;
          background: linear-gradient(135deg, hsl(var(--accent)) 0%, color-mix(in srgb, hsl(var(--accent)) 80%, black) 100%);
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 0 20px hsl(var(--accent) / 0.2);
        }

        .verify-button:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 0 30px hsl(var(--accent) / 0.3);
        }

        .verify-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .verify-button .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: hsl(var(--accent-foreground));
          font-family: 'Satoshi', system-ui, sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
        }
      `}</style>
    </form>
  )
}
